import React, { useEffect, useState } from 'react';
import { string, bool, func } from 'prop-types';
import { text } from 'polyglot/polyglot';
import { truncate } from 'lodash';
import ChatManager from 'socket/ChatManager';
import useChatTextStore from 'stores/chat-text';
import { useChatStore } from 'stores/chat';
import useUserStore from 'stores/user';
import bingoAssetsStore from 'stores/bingoAssetsStore';
import clsx from 'clsx';

import Modal from 'components/Modal/Modal';
import Avatar from 'components/Chat/Avatar/Avatar';
import Button from 'components/Button/Button';

import UserIcon from 'themes/common/svg/user.svg';

import UserList from './UserList/UserList';

import styles from './GroupChatHeader.module.scss';

const currentUserSelector = (state) => state.user;
const chatroomsSelector = (state) => state.chatrooms;
const userIsBlockedSelector = (state) => state.userIsBlocked;
const lastUpdatedSelector = (state) => state.lastUpdated;
const fetchAssetSelector = (state) => state.fetchAsset;
const getAssetSelector = (state) => state.assets;

function GroupChatHeader({
  currentRoomId,
  cantRequest,
  hasUserList,
  setHasUserList,
}) {
  const isExpanded = useChatStore((state) => state.isChatExpanded);

  const currentUser = useUserStore(currentUserSelector);
  const [hasModal, setHasModal] = useState(false);
  const [privateChatRequestedUser, setPrivateChatRequestedUser] =
    useState(null);
  const [blockRequestedUser, setBlockRequestedUser] = useState(null);
  const [unblockRequestedUser, setUnblockRequestedUser] = useState(null);
  const chatrooms = useChatStore(chatroomsSelector);
  const [admin, setAdmin] = useState(null);
  const [regularUsers, setRegularUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const userIsBlocked = useChatTextStore(userIsBlockedSelector);
  const lastUpdated = useChatStore(lastUpdatedSelector);
  const [hasPrivateChat, setHasPrivateChat] = useState(true);

  const fetchAsset = bingoAssetsStore(fetchAssetSelector);
  const assets = bingoAssetsStore(getAssetSelector);

  useEffect(() => {
    if (currentRoomId) {
      const newRoomIndex = chatrooms.findIndex(
        (room) => room.roomId === currentRoomId
      );
      setHasPrivateChat(chatrooms[newRoomIndex].allowsPrivateChat);
      setAdmin(
        chatrooms[newRoomIndex].chatParticipants.find((u) => u.isChatHost)
      );
      setRegularUsers(
        chatrooms[newRoomIndex].chatParticipants.filter(
          (u) => !u.isChatHost && u.nickname !== currentUser.nickname
        )
      );
      setUserCount(chatrooms[newRoomIndex].chatParticipantCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomId, lastUpdated]);

  useEffect(() => {
    if (admin?.chatHostImage) {
      fetchAsset(admin.nickname, admin.chatHostImage);
    }
  }, [admin, fetchAsset]);

  const chathostImg = admin ? assets[admin.nickname] : null;

  const onClickUserList = () => {
    ChatManager.instance.requestChatParticipiantList(currentRoomId);
    setHasUserList(!hasUserList);
  };

  const onClickUser = (userName) => {
    if (!currentUser.isAuthenticated || userIsBlocked) return;
    setHasUserList(false);
    setBlockRequestedUser(null);
    setPrivateChatRequestedUser(userName);
    setHasModal(true);
  };

  const onClickBlock = (userName) => {
    if (!currentUser.isAuthenticated || userIsBlocked) return;
    setHasUserList(false);
    setPrivateChatRequestedUser(null);
    setUnblockRequestedUser(null);
    setBlockRequestedUser(userName);
    setBlockedUsers((prev) => [...prev, userName]);
    setHasModal(true);
  };

  const onClickUnblock = (userName) => {
    if (!currentUser.isAuthenticated || userIsBlocked) return;
    setHasUserList(false);
    setPrivateChatRequestedUser(null);
    setBlockRequestedUser(null);
    setUnblockRequestedUser(userName);
    setBlockedUsers((prev) => prev.filter((u) => u !== userName));
    setHasModal(true);
  };

  const onCloseModal = () => {
    setHasModal(false);
    setPrivateChatRequestedUser(null);
    setBlockRequestedUser(null);
  };

  const sendRequestToUser = () => {
    ChatManager.instance.requestPrivateChat(privateChatRequestedUser);
    onCloseModal();
  };

  const blockChatUser = () => {
    ChatManager.instance.blockPrivateChatUser(blockRequestedUser);
    onCloseModal();
  };

  const unblockChatUser = () => {
    ChatManager.instance.unblockPrivateChatUser(unblockRequestedUser);
    onCloseModal();
  };

  const modalContent = (
    <div className={styles.reqText}>
      <p>
        {(() => {
          if (privateChatRequestedUser) {
            return text.t('chat.modal.startPrivateChat.text');
          }
          if (unblockRequestedUser) {
            return text.t('chat.modal.unblockChatUser.text');
          }
          return text.t('chat.modal.blockChatUser.text');
        })()}
      </p>
      <p>
        <b className={styles.reqUser}>
          {truncate(
            privateChatRequestedUser ??
              blockRequestedUser ??
              unblockRequestedUser,
            {
              length: 16,
            }
          )}
        </b>
        ?
      </p>
    </div>
  );

  const secondaryModalContent = (
    <div className={styles.reqText}>
      <p>{text.t('chat.modal.secondaryText')}</p>
    </div>
  );

  const modalButtons = [
    <Button
      key="cancel-button"
      className={styles.button}
      onClick={onCloseModal}
      isSecondary
    >
      {text.t('chat.modal.cancelButton')}
    </Button>,
    <Button
      key="confirm-button"
      className={styles.button}
      onClick={() => {
        switch (true) {
          case !!privateChatRequestedUser:
            sendRequestToUser();
            break;
          case !!blockRequestedUser:
            blockChatUser();
            break;
          case !!unblockRequestedUser:
            unblockChatUser();
            break;
          default:
            break;
        }
      }}
    >
      {text.t('chat.modal.confirmButton')}
    </Button>,
  ];

  const secondaryModalButtons = [
    <Button
      key="cancel-button"
      className={styles.button}
      onClick={onCloseModal}
      isSecondary
    >
      {text.t('chat.modal.okButton')}
    </Button>,
  ];

  // Close user list on toggle.
  useEffect(() => {
    if (hasUserList && !isExpanded) {
      setHasUserList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUserList, isExpanded]);

  return (
    <>
      <div
        className={clsx(styles.wrapper, {
          [styles.hasAdmin]: admin,
        })}
      >
        {admin && (
          <div className={styles.admin}>
            {chathostImg ? (
              <div className={styles.chathostImgWrapper}>
                <img src={chathostImg} alt="" />
              </div>
            ) : (
              <Avatar avatar={admin.avatarId} className={styles.adminAvatar} />
            )}

            <div className={styles.adminInfo}>
              <span className={styles.adminLabel}>{text.t('chat.admin')}</span>
              &nbsp;
              <span className={styles.adminName}>{admin.nickname}</span>
            </div>
          </div>
        )}

        <button
          className={styles.usersButton}
          type="button"
          onClick={onClickUserList}
          disabled={userCount === 1}
        >
          <span className={styles.userCount}>{`${userCount} ${text.t(
            'chat.amountSuffix'
          )}`}</span>
          <UserIcon className={styles.userIcon} />
        </button>
      </div>

      {hasModal &&
        (hasPrivateChat ? (
          <Modal
            title={(() => {
              if (privateChatRequestedUser) {
                return text.t('chat.modal.startPrivateChat.title');
              }
              if (blockRequestedUser) {
                return text.t('chat.modal.blockChatUser.title');
              }
              return text.t('chat.modal.unblockChatUser.title');
            })()}
            content={cantRequest ? secondaryModalContent : modalContent}
            buttons={cantRequest ? secondaryModalButtons : modalButtons}
          />
        ) : null)}

      {regularUsers.length > 0 && (
        <UserList
          users={regularUsers}
          blockedUsers={blockedUsers}
          isOpen={hasUserList}
          onClickArray={[onClickUser, onClickBlock, onClickUnblock]}
        />
      )}
    </>
  );
}

GroupChatHeader.propTypes = {
  currentRoomId: string.isRequired,
  cantRequest: bool.isRequired,
  hasUserList: bool.isRequired,
  setHasUserList: func.isRequired,
};

export default GroupChatHeader;
